"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  incidenceRate,
  proportionOf,
  RATE_BASES,
  type RateBase,
} from "@/lib/tool-kit/stats-research/epidemiology";
import { type Formatter, fixed } from "@/lib/tool-kit/stats-research/format";
import { poissonCountInterval } from "@/lib/tool-kit/stats-research/proportions";

/** English strings. A translated content file overrides these through `ui`. */
export const INCIDENCE_PREVALENCE_CALCULATOR_STRINGS = {
  modeLabel: "Measure",
  modeRisk: "Cumulative incidence (risk)",
  modeRate: "Incidence rate (person-time)",
  modePrevalence: "Prevalence",
  casesNew: "New cases during the period",
  atRisk: "People at risk at the start",
  casesRate: "New cases",
  personTime: "Person-time at risk",
  unitLabel: "Person-time unit",
  unitYear: "person-year",
  unitYears: "person-years",
  unitMonth: "person-month",
  unitMonths: "person-months",
  unitWeek: "person-week",
  unitWeeks: "person-weeks",
  unitDay: "person-day",
  unitDays: "person-days",
  casesExisting: "People with the condition",
  population: "Population",
  typeLabel: "Type of prevalence",
  typePoint: "Point prevalence — at one moment",
  typePeriod: "Period prevalence — at any time in a period",
  baseLabel: "Express per",
  numberHint: "Type numbers without thousands separators: 9400, not 9,400.",
  loadExample: "Load example",
  clear: "Clear",
  results: "Results",
  riskHeadline: "Cumulative incidence per {base}",
  rateHeadline: "Incidence rate per {base} {units}",
  pointHeadline: "Point prevalence per {base}",
  periodHeadline: "Period prevalence per {base}",
  intervalLabel: "95% CI per {base}",
  percentLabel: "As a percentage",
  perUnitLabel: "Per {unit}",
  casesLabel: "Cases",
  riskNote:
    "Cumulative incidence assumes everyone was free of the condition at the start and was followed for the whole period. When people drop out or join late, use the incidence rate instead.",
  rateNote:
    "The interval is exact: the number of cases is treated as a Poisson count and the limits come from chi-square quantiles, so it stays valid for small counts, even zero.",
  pointNote:
    "Point prevalence counts everyone who has the condition at one moment — a survey date — divided by the population at that moment.",
  periodNote:
    "Period prevalence counts everyone who had the condition at any time in the period, existing and new cases alike, usually divided by the average or mid-period population.",
  howCalculated: "How this was calculated",
  workingProportion:
    "{cases} / {people} = {fraction} = {scaled} per {base}; Wilson 95% CI {lower} to {upper} per {base}",
  workingRate:
    "{cases} / {time} {units} = {fraction} per {unit} = {scaled} per {base} {units}",
  workingPoisson:
    "Exact 95% limits for {cases} cases: χ²(0.025; 2 × {cases}) / 2 = {countLower} and χ²(0.975; 2 × {cases} + 2) / 2 = {countUpper}, divided by the person-time: {lower} to {upper} per {base} {units}",
  report: "{label}: {value} (95% CI {lower}–{upper}).",
  copyResult: "Copy result",
  copied: "Copied!",
  promptProportion:
    "Enter the number of cases and the population to see the result.",
  promptRate: "Enter the number of new cases and the person-time at risk.",
  invalidProportion:
    "Cases must be a whole number from 0 up to the population, and the population a whole number above 0.",
  invalidRate:
    "Cases must be a whole number, zero or more, and person-time must be greater than 0.",
} as const;

type Strings = typeof INCIDENCE_PREVALENCE_CALCULATOR_STRINGS;
type Mode = "risk" | "rate" | "prevalence";
type Unit = "years" | "months" | "weeks" | "days";
type Kind = "point" | "period";

const UNIT_KEYS: Record<Unit, [keyof Strings, keyof Strings]> = {
  years: ["unitYear", "unitYears"],
  months: ["unitMonth", "unitMonths"],
  weeks: ["unitWeek", "unitWeeks"],
  days: ["unitDay", "unitDays"],
};

const EXAMPLES: Record<Mode, [string, string]> = {
  risk: ["120", "2000"],
  rate: ["120", "9400"],
  prevalence: ["340", "4000"],
};

interface Outcome {
  headline: string;
  value: number;
  lower: number;
  upper: number;
  fraction: number;
  working: string[];
  note: string;
}

function proportionOutcome(
  cases: number,
  people: number,
  base: RateBase,
  headline: string,
  note: string,
  strings: Strings,
  fmt: Formatter
): Outcome | null {
  const estimate = proportionOf(cases, people);
  if (!estimate) {
    return null;
  }
  const two = (value: number) => fixed(fmt, value * base, 2);
  return {
    headline,
    value: estimate.value * base,
    lower: estimate.lower * base,
    upper: estimate.upper * base,
    fraction: estimate.value,
    note,
    working: [
      t(strings.workingProportion, {
        cases: fmt(cases),
        people: fmt(people),
        fraction: fixed(fmt, estimate.value, 4),
        scaled: two(estimate.value),
        base: fmt(base),
        lower: two(estimate.lower),
        upper: two(estimate.upper),
      }),
    ],
  };
}

function proportionTexts(
  mode: Mode,
  kind: Kind,
  strings: Strings
): { headline: string; note: string } {
  if (mode === "risk") {
    return { headline: strings.riskHeadline, note: strings.riskNote };
  }
  if (kind === "point") {
    return { headline: strings.pointHeadline, note: strings.pointNote };
  }
  return { headline: strings.periodHeadline, note: strings.periodNote };
}

function rateOutcome(
  cases: number,
  time: number,
  base: RateBase,
  unit: Unit,
  strings: Strings,
  fmt: Formatter
): Outcome | null {
  const rate = incidenceRate(cases, time);
  if (!rate) {
    return null;
  }
  const [one, many] = UNIT_KEYS[unit];
  const units = strings[many];
  const two = (value: number) => fixed(fmt, value * base, 2);
  const counts = poissonCountInterval(cases);
  return {
    headline: t(strings.rateHeadline, { base: fmt(base), units }),
    value: rate.value * base,
    lower: rate.lower * base,
    upper: rate.upper * base,
    fraction: rate.value,
    note: strings.rateNote,
    working: [
      t(strings.workingRate, {
        cases: fmt(cases),
        time: fmt(time),
        units,
        unit: strings[one],
        fraction: fixed(fmt, rate.value, 6),
        scaled: two(rate.value),
        base: fmt(base),
      }),
      t(strings.workingPoisson, {
        cases: fmt(cases),
        countLower: fixed(fmt, counts.lower, 2),
        countUpper: fixed(fmt, counts.upper, 2),
        lower: two(rate.lower),
        upper: two(rate.upper),
        base: fmt(base),
        units,
      }),
    ],
  };
}

function Results({
  outcome,
  mode,
  unit,
  strings,
  fmt,
  base,
}: {
  outcome: Outcome;
  mode: Mode;
  unit: Unit;
  strings: Strings;
  fmt: Formatter;
  base: RateBase;
}) {
  const two = (value: number) => fixed(fmt, value, 2);
  const report = t(strings.report, {
    label: outcome.headline,
    value: two(outcome.value),
    lower: two(outcome.lower),
    upper: two(outcome.upper),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={outcome.headline}
          value={two(outcome.value)}
        />
        <StatCard
          label={t(strings.intervalLabel, { base: fmt(base) })}
          value={`${two(outcome.lower)} – ${two(outcome.upper)}`}
        />
        {mode === "rate" ? (
          <StatCard
            label={t(strings.perUnitLabel, {
              unit: strings[UNIT_KEYS[unit][0]],
            })}
            value={fixed(fmt, outcome.fraction, 6)}
          />
        ) : (
          <StatCard
            label={strings.percentLabel}
            value={`${fixed(fmt, outcome.fraction * 100, 2)}%`}
          />
        )}
      </StatGrid>
      <Notice>{outcome.note}</Notice>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        {outcome.working.map((line) => (
          <p
            className="font-mono text-muted-foreground text-sm leading-relaxed"
            key={line}
          >
            {line}
          </p>
        ))}
      </div>
    </section>
  );
}

export default function IncidencePrevalenceCalculatorWidget() {
  const strings = useToolUi(INCIDENCE_PREVALENCE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("risk");
  const [inputs, setInputs] = useState<Record<Mode, [string, string]>>({
    risk: ["", ""],
    rate: ["", ""],
    prevalence: ["", ""],
  });
  const [unit, setUnit] = useState<Unit>("years");
  const [kind, setKind] = useState<Kind>("point");
  const [baseText, setBaseText] = useState("1000");

  const base = (RATE_BASES.find((value) => String(value) === baseText) ??
    1000) as RateBase;
  const [first, second] = inputs[mode];
  const cases = parseIntegerInput(first);
  const denominator =
    mode === "rate" ? parseNumberInput(second) : parseIntegerInput(second);
  const entered = first.trim() !== "" && second.trim() !== "";

  let outcome: Outcome | null = null;
  if (entered && cases !== null && denominator !== null) {
    if (mode === "rate") {
      outcome = rateOutcome(cases, denominator, base, unit, strings, fmt);
    } else {
      const texts = proportionTexts(mode, kind, strings);
      outcome = proportionOutcome(
        cases,
        denominator,
        base,
        t(texts.headline, { base: fmt(base) }),
        texts.note,
        strings,
        fmt
      );
    }
  }

  const setField = (index: 0 | 1) => (value: string) =>
    setInputs((current) => {
      const [a, b] = current[mode];
      const next: [string, string] = index === 0 ? [value, b] : [a, value];
      return { ...current, [mode]: next };
    });
  const labels: Record<Mode, [string, string]> = {
    risk: [strings.casesNew, strings.atRisk],
    rate: [strings.casesRate, strings.personTime],
    prevalence: [strings.casesExisting, strings.population],
  };

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ip-mode"
        onChange={setMode}
        options={[
          { value: "risk", label: strings.modeRisk },
          { value: "rate", label: strings.modeRate },
          { value: "prevalence", label: strings.modePrevalence },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="ip-cases"
          label={labels[mode][0]}
          numeric
          onChange={setField(0)}
          placeholder={EXAMPLES[mode][0]}
          value={first}
        />
        <TextField
          hint={strings.numberHint}
          id="ip-denominator"
          label={labels[mode][1]}
          numeric
          onChange={setField(1)}
          placeholder={EXAMPLES[mode][1]}
          value={second}
        />
        {mode === "rate" ? (
          <SelectField
            id="ip-unit"
            label={strings.unitLabel}
            onChange={setUnit}
            options={(Object.keys(UNIT_KEYS) as Unit[]).map((value) => ({
              value,
              label: strings[UNIT_KEYS[value][1]],
            }))}
            value={unit}
          />
        ) : null}
        {mode === "prevalence" ? (
          <SelectField
            id="ip-kind"
            label={strings.typeLabel}
            onChange={setKind}
            options={[
              { value: "point", label: strings.typePoint },
              { value: "period", label: strings.typePeriod },
            ]}
            value={kind}
          />
        ) : null}
        <SelectField
          id="ip-base"
          label={strings.baseLabel}
          onChange={setBaseText}
          options={RATE_BASES.map((value) => ({
            value: String(value),
            label: fmt(value),
          }))}
          value={baseText}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setInputs((current) => ({ ...current, [mode]: EXAMPLES[mode] }))
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setInputs((current) => ({ ...current, [mode]: ["", ""] }))
          }
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {outcome ? (
        <Results
          base={base}
          fmt={fmt}
          mode={mode}
          outcome={outcome}
          strings={strings}
          unit={unit}
        />
      ) : null}
      {entered && !outcome ? (
        <ErrorNotice>
          {mode === "rate" ? strings.invalidRate : strings.invalidProportion}
        </ErrorNotice>
      ) : null}
      {entered ? null : (
        <Notice>
          {mode === "rate" ? strings.promptRate : strings.promptProportion}
        </Notice>
      )}
    </div>
  );
}
